import { useState, useEffect, useCallback, useMemo } from 'react';
import { crmService } from '../../services/crmService';
import { useIsHQ, useIdentity } from '../../hooks/useIsHQ';
import TeamImport from './TeamImport';

const STAGE_COLORS = {
  concept: '#6B7280', design: '#3B82F6', prototype: '#8B5CF6', testing: '#F59E0B',
  demonstration: '#EA580C', commercialization: '#c4a563', fielded: '#10B981', shelved: '#374151',
};

// Organizational teams — open taxonomy, refined as the platform evolves
const TEAM_GROUPS = [
  { key: 'headquarters', label: 'Headquarters' },
  { key: 'build',        label: 'Build Team' },
  { key: 'growth',       label: 'Growth Team' },
  { key: 'bd',           label: 'BD Team' },
  { key: 'operations',   label: 'Operations' },
  { key: 'other',        label: 'Other' },
];
const groupLabel = (key) => TEAM_GROUPS.find(g => g.key === key)?.label || (key ? key : 'Unassigned');

const inp = { background: '#0A0F1C', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '6px 10px', borderRadius: 4, fontSize: 12, width: '100%', boxSizing: 'border-box' };
const lbl = { color: '#94A3B8', fontSize: 11, display: 'block', marginBottom: 3 };
const card = { background: '#111827', border: '1px solid #1E2D45', borderRadius: 8 };

const allocTotal = (m) => (m.assignments || [])
  .filter(a => a.project_status === 'active')
  .reduce((s, a) => s + (a.allocation_pct || 0), 0);

// Crop-center and resize an image file to a 128px JPEG data URL (~10-15KB)
const fileToAvatar = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const size = 128;
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    const min = Math.min(img.width, img.height);
    ctx.drawImage(img, (img.width - min) / 2, (img.height - min) / 2, min, min, 0, 0, size, size);
    URL.revokeObjectURL(url);
    resolve(canvas.toDataURL('image/jpeg', 0.85));
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read image')); };
  img.src = url;
});

const Avatar = ({ member, size = 30 }) => member.avatar_url ? (
  <img src={member.avatar_url} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid #1E2D45' }} />
) : (
  <span style={{
    width: size, height: size, borderRadius: '50%', background: '#0A0F1C', flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    color: '#22D3EE', fontWeight: 700, fontSize: Math.max(9, size * 0.4),
  }}>
    {(member.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
  </span>
);

function MemberModal({ member, subsidiaries, isHQ, onClose, onSaved }) {
  const isEdit = !!member?.id;
  const [form, setForm] = useState(() => ({
    name: member?.name || '', email: member?.email || '',
    position_title: member?.position_title || '', subsidiary_id: member?.subsidiary_id || '',
    duties: member?.duties || '', skills: member?.skills || '', clearance: member?.clearance || '',
    capacity_pct: member?.capacity_pct ?? 100, status: member?.status || 'active', notes: member?.notes || '',
    team_group: member?.team_group || '', avatar_url: member?.avatar_url || null,
    phone: member?.phone || '', alt_email: member?.alt_email || '', emergency_contact: member?.emergency_contact || '',
    is_subsidiary_lead: member?.is_subsidiary_lead || false,
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { set('avatar_url', await fileToAvatar(file)); }
    catch { setError('Could not read that image — try a JPG or PNG'); }
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isHQ && !form.name.trim()) { setError('Name required'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, subsidiary_id: form.subsidiary_id ? parseInt(form.subsidiary_id) : null, capacity_pct: parseInt(form.capacity_pct) || 100 };
      const saved = isEdit
        ? await crmService.updateTeamMember(member.id, payload)
        : await crmService.createTeamMember(payload);
      onSaved(saved);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ ...card, width: 600, maxHeight: '88vh', display: 'flex', flexDirection: 'column', color: '#F1F5F9' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #1E2D45', display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 14, color: '#22D3EE' }}>
            {isEdit ? (isHQ ? 'Edit Member' : 'Define My Position') : 'Add Family Member'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <Avatar member={{ name: form.name || member?.name, avatar_url: form.avatar_url }} size={52} />
            <label style={{ padding: '5px 12px', background: '#0D1F3C', border: '1px solid #22D3EE44', color: '#22D3EE', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
              {form.avatar_url ? 'Change Photo' : 'Upload Photo'}
              <input type="file" accept="image/*" onChange={handlePhoto} style={{ display: 'none' }} />
            </label>
            {form.avatar_url && (
              <button type="button" onClick={() => set('avatar_url', null)}
                style={{ background: 'none', border: '1px solid #1E2D45', color: '#6B7280', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', fontSize: 11 }}>
                Remove
              </button>
            )}
            <span style={{ fontSize: 9, color: '#4B5563' }}>Shown on the roster and project team chips</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 10 }}>
            {isHQ && (
              <>
                <div>
                  <label style={lbl}>Name *</label>
                  <input style={inp} value={form.name} onChange={e => set('name', e.target.value)} required />
                </div>
                <div>
                  <label style={lbl}>Email</label>
                  <input style={inp} value={form.email} onChange={e => set('email', e.target.value)} placeholder="links their login to this profile" />
                </div>
              </>
            )}
            <div>
              <label style={lbl}>Position / Title</label>
              <input style={inp} value={form.position_title} onChange={e => set('position_title', e.target.value)} placeholder="e.g. Principal RF Engineer" />
            </div>
            {isHQ && (
              <div>
                <label style={lbl}>Team (HQ-assigned)</label>
                <select style={inp} value={form.team_group} onChange={e => set('team_group', e.target.value)}>
                  <option value="">— Unassigned —</option>
                  {TEAM_GROUPS.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
                </select>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, color: '#c4a563', marginTop: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.is_subsidiary_lead} onChange={e => set('is_subsidiary_lead', e.target.checked)} />
                  Subsidiary Element Lead (can bulk-import contacts)
                </label>
              </div>
            )}
            {isHQ ? (
              <div>
                <label style={lbl}>Subsidiary</label>
                <select style={inp} value={form.subsidiary_id || ''} onChange={e => set('subsidiary_id', e.target.value)}>
                  <option value="">— None —</option>
                  {subsidiaries.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label style={lbl}>Available Capacity %</label>
                <input type="number" min={0} max={100} style={inp} value={form.capacity_pct} onChange={e => set('capacity_pct', e.target.value)} />
              </div>
            )}
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={lbl}>Duties & Responsibilities</label>
            <textarea style={{ ...inp, height: 80, resize: 'vertical' }} value={form.duties} onChange={e => set('duties', e.target.value)}
              placeholder="What this role owns — programs, deliverables, decisions" />
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={lbl}>Skills & Competencies</label>
            <textarea style={{ ...inp, height: 52, resize: 'vertical' }} value={form.skills} onChange={e => set('skills', e.target.value)}
              placeholder="e.g. RF design, Python, SIGINT analysis, proposal writing" />
          </div>
          <div style={{ marginTop: 12, border: '1px solid #c4a56333', borderRadius: 6, padding: '10px 12px' }}>
            <div style={{ fontSize: 9, color: '#c4a563', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
              🔒 Alternate Contact Details — visible only to you and Headquarters
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 10 }}>
              <div>
                <label style={lbl}>Phone</label>
                <input style={inp} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 …" />
              </div>
              <div>
                <label style={lbl}>Alternate Email</label>
                <input style={inp} value={form.alt_email} onChange={e => set('alt_email', e.target.value)} />
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={lbl}>Emergency Contact</label>
              <input style={inp} value={form.emergency_contact} onChange={e => set('emergency_contact', e.target.value)} placeholder="Name · relationship · phone" />
            </div>
          </div>
          {isHQ && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: 10 }}>
              <div>
                <label style={lbl}>Clearance</label>
                <input style={inp} value={form.clearance} onChange={e => set('clearance', e.target.value)} placeholder="e.g. TS/SCI" />
              </div>
              <div>
                <label style={lbl}>Capacity %</label>
                <input type="number" min={0} max={100} style={inp} value={form.capacity_pct} onChange={e => set('capacity_pct', e.target.value)} />
              </div>
              <div>
                <label style={lbl}>Status</label>
                <select style={inp} value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </div>
            </div>
          )}
          {error && <div style={{ color: '#EF4444', fontSize: 12, marginTop: 8 }}>{error}</div>}
        </form>
        <div style={{ padding: '10px 18px', borderTop: '1px solid #1E2D45', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '6px 14px', background: '#111827', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving} style={{ padding: '6px 14px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RequestMemberModal({ onClose }) {
  const [form, setForm] = useState({ name: '', email: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { setError('Name and email required'); return; }
    setBusy(true); setError('');
    try {
      await crmService.requestTeamMember(form);
      alert('Request sent — Headquarters has been notified and will review it in their inbox.');
      onClose();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ ...card, width: 460, padding: '18px 20px', color: '#F1F5F9' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, color: '#22D3EE' }}>Request a New Family Member</h3>
        <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 14 }}>
          Headquarters reviews every request — they'll be tagged by email and in their inbox.
        </div>
        <form onSubmit={submit}>
          <label style={lbl}>Name *</label>
          <input style={{ ...inp, marginBottom: 10 }} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
          <label style={lbl}>Email *</label>
          <input type="email" style={{ ...inp, marginBottom: 10 }} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
          <label style={lbl}>Why should they join?</label>
          <textarea style={{ ...inp, height: 64, resize: 'vertical', marginBottom: 12 }} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Role they'd fill, effort they'd support…" />
          {error && <div style={{ color: '#EF4444', fontSize: 12, marginBottom: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '6px 14px', background: 'none', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
            <button type="submit" disabled={busy} style={{ padding: '6px 14px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}>
              {busy ? 'Sending…' : 'Send Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MemberRow({ member, isHQ, isSelf, projects, onEdit, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [assignForm, setAssignForm] = useState(null); // { project_id, role, allocation_pct }
  const [updateText, setUpdateText] = useState('');
  const [updateFor, setUpdateFor] = useState('');
  const [busy, setBusy] = useState(false);

  const assignments = (member.assignments || []).filter(a => a.project_status !== 'cancelled');
  const total = allocTotal(member);
  const over = total > member.capacity_pct;
  const assignable = projects.filter(p => p.status === 'active' && !assignments.some(a => a.project_id === p.id));

  const assign = async () => {
    if (!assignForm?.project_id) return;
    setBusy(true);
    try {
      await crmService.assignTeamMember(member.id, { ...assignForm, project_id: parseInt(assignForm.project_id), allocation_pct: parseInt(assignForm.allocation_pct) || 25 });
      setAssignForm(null);
      await onChanged();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  const postUpdate = async () => {
    if (!updateFor || !updateText.trim()) return;
    setBusy(true);
    try {
      await crmService.postTechProgressUpdate(parseInt(updateFor), updateText.trim());
      setUpdateText(''); setUpdateFor('');
      alert(isHQ ? 'Progress logged — it shows on the effort in Tusks.' : 'Progress logged — leadership and your team can see it on the effort.');
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ ...card, marginBottom: 8, overflow: 'hidden', borderLeft: isSelf ? '3px solid #22D3EE' : '3px solid transparent' }}>
      <div onClick={() => setExpanded(x => !x)} style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Avatar member={member} size={30} />
        <div style={{ minWidth: 150 }}>
          <span style={{ color: '#F1F5F9', fontSize: 13, fontWeight: 600 }}>{member.name}</span>
          {isSelf && <span style={{ color: '#22D3EE', fontSize: 9, marginLeft: 6, fontWeight: 700 }}>YOU</span>}
          {member.status !== 'active' && <span style={{ color: '#6B7280', fontSize: 9, marginLeft: 6 }}>inactive</span>}
        </div>
        <span style={{ color: '#94A3B8', fontSize: 11, flex: 1, minWidth: 120 }}>
          {member.position_title || <span style={{ color: '#4B5563', fontStyle: 'italic' }}>position undefined</span>}
          {member.subsidiary_name ? <span style={{ color: '#4B5563' }}> · {member.subsidiary_name}</span> : null}
        </span>
        {member.team_group && (
          <span style={{ fontSize: 9, color: '#94A3B8', border: '1px solid #1E2D45', borderRadius: 8, padding: '1px 8px', flexShrink: 0 }}>
            {groupLabel(member.team_group)}
          </span>
        )}
        {member.is_subsidiary_lead && (
          <span title="Subsidiary element lead — can bulk-import contacts" style={{ fontSize: 9, fontWeight: 700, color: '#c4a563', border: '1px solid #c4a56344', borderRadius: 8, padding: '1px 8px', flexShrink: 0 }}>LEAD</span>
        )}
        {!member.user_id && member.invited_at && (
          <span style={{ fontSize: 9, color: '#c4a563', border: '1px solid #c4a56344', borderRadius: 8, padding: '1px 8px', flexShrink: 0 }}>invited</span>
        )}
        {isHQ && !member.user_id && member.email && (
          <button
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const r = await crmService.inviteTeamMember(member.id);
                window.prompt(
                  `Invitation created — email ${r.email_sent ? 'sent to ' + member.email : 'NOT sent (service unavailable)'}${r.sms_sent ? ', text sent' : ''}. Copy the link:`,
                  r.invite_url
                );
                onChanged();
              } catch (err) { alert(err.message); }
            }}
            style={{ fontSize: 9, fontWeight: 700, color: '#22D3EE', background: '#0D1F3C', border: '1px solid #22D3EE44', borderRadius: 8, padding: '2px 9px', cursor: 'pointer', flexShrink: 0 }}
          >
            {member.invited_at ? 'Re-invite' : 'Invite'}
          </button>
        )}
        {/* capacity bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: 150, flexShrink: 0 }}>
          <div style={{ flex: 1, height: 5, background: '#0A0F1C', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(100, (total / Math.max(member.capacity_pct, 1)) * 100)}%`, background: over ? '#EF4444' : total > 0 ? '#22D3EE' : '#1E2D45' }} />
          </div>
          <span style={{ fontSize: 9, color: over ? '#EF4444' : '#6B7280', whiteSpace: 'nowrap' }}>{total}/{member.capacity_pct}%</span>
        </div>
        <span style={{ fontSize: 10, color: '#6B7280', flexShrink: 0 }}>{assignments.length} effort{assignments.length !== 1 ? 's' : ''}</span>
        <span style={{ color: '#4B5563', fontSize: 11 }}>{expanded ? '▾' : '▸'}</span>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid #1E2D45', padding: '12px 14px', background: '#0D1526' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 18 }}>
            <div>
              <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Duties & Responsibilities</div>
              <div style={{ fontSize: 12, color: member.duties ? '#94A3B8' : '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {member.duties || (isSelf ? 'Define your role — click Edit below.' : 'Not yet defined.')}
              </div>
              {member.skills && (
                <>
                  <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '10px 0 4px' }}>Skills</div>
                  <div style={{ fontSize: 11, color: '#94A3B8' }}>{member.skills}</div>
                </>
              )}
              {member.clearance && (
                <div style={{ fontSize: 10, color: '#c4a563', marginTop: 8 }}>Clearance: {member.clearance}</div>
              )}
              {(member.phone || member.alt_email || member.emergency_contact) && (
                <div style={{ marginTop: 10, border: '1px solid #c4a56322', borderRadius: 6, padding: '8px 10px' }}>
                  <div style={{ fontSize: 9, color: '#c4a563', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                    🔒 Contact Details {isHQ && !isSelf ? '(HQ view)' : '(only you and HQ see this)'}
                  </div>
                  {member.phone && <div style={{ fontSize: 11, color: '#94A3B8' }}>Phone: {member.phone}</div>}
                  {member.alt_email && <div style={{ fontSize: 11, color: '#94A3B8' }}>Alt email: {member.alt_email}</div>}
                  {member.emergency_contact && <div style={{ fontSize: 11, color: '#94A3B8' }}>Emergency: {member.emergency_contact}</div>}
                </div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Assigned Efforts{isHQ ? ' (Tusks)' : ''}</div>
              {assignments.length === 0 && <div style={{ fontSize: 11, color: '#374151' }}>Not assigned to any development effort.</div>}
              {assignments.map(a => (
                <div key={a.project_id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: STAGE_COLORS[a.project_stage] || '#6B7280', flexShrink: 0, alignSelf: 'center' }} />
                  <span style={{ fontSize: 12, color: '#F1F5F9', flex: 1 }}>{a.project_name}</span>
                  {a.role && <span style={{ fontSize: 10, color: '#94A3B8' }}>{a.role}</span>}
                  <span style={{ fontSize: 10, color: '#22D3EE' }}>{a.allocation_pct}%</span>
                  {isHQ && (
                    <button onClick={() => crmService.unassignTeamMember(member.id, a.project_id).then(onChanged)}
                      style={{ background: 'none', border: 'none', color: '#4B5563', cursor: 'pointer', fontSize: 12 }}>×</button>
                  )}
                </div>
              ))}
              {isHQ && (assignForm ? (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  <select value={assignForm.project_id} onChange={e => setAssignForm(f => ({ ...f, project_id: e.target.value }))} style={{ ...inp, flex: 1, minWidth: 140 }}>
                    <option value="">Effort…</option>
                    {assignable.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <input style={{ ...inp, width: 110 }} placeholder="Role" value={assignForm.role} onChange={e => setAssignForm(f => ({ ...f, role: e.target.value }))} />
                  <input type="number" min={0} max={100} style={{ ...inp, width: 64 }} value={assignForm.allocation_pct} onChange={e => setAssignForm(f => ({ ...f, allocation_pct: e.target.value }))} />
                  <button onClick={assign} disabled={busy || !assignForm.project_id} style={{ padding: '4px 12px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Assign</button>
                  <button onClick={() => setAssignForm(null)} style={{ padding: '4px 8px', background: 'none', border: '1px solid #1E2D45', color: '#6B7280', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>✕</button>
                </div>
              ) : (
                <button onClick={() => setAssignForm({ project_id: '', role: '', allocation_pct: 25 })}
                  style={{ marginTop: 8, background: 'none', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 11 }}>
                  + Assign to Effort
                </button>
              ))}

              {(isSelf || isHQ) && assignments.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Log Progress</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <select value={updateFor} onChange={e => setUpdateFor(e.target.value)} style={{ ...inp, width: 150 }}>
                      <option value="">Effort…</option>
                      {assignments.map(a => <option key={a.project_id} value={a.project_id}>{a.project_name}</option>)}
                    </select>
                    <input style={{ ...inp, flex: 1, minWidth: 160 }} placeholder="What moved forward…" value={updateText} onChange={e => setUpdateText(e.target.value)} />
                    <button onClick={postUpdate} disabled={busy || !updateFor || !updateText.trim()}
                      style={{ padding: '4px 12px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Post</button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {(isSelf || isHQ) && (
            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => onEdit(member)} style={{ background: 'none', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, padding: '4px 14px', cursor: 'pointer', fontSize: 11 }}>
                {isSelf && !isHQ ? 'Define My Position' : 'Edit'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TheFamily() {
  const isHQ = useIsHQ();
  const me = useIdentity();
  const [team, setTeam] = useState([]);
  const [projects, setProjects] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [future, setFuture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [groupFilter, setGroupFilter] = useState(null); // null until tailored to the viewer's team

  const load = useCallback(() => Promise.all([
    crmService.getTeam().then(setTeam),
    crmService.getFutureState().then(setFuture).catch(() => setFuture(null)),
  ]), []);

  useEffect(() => {
    Promise.all([
      load(),
      isHQ ? crmService.getTechProjects().then(setProjects).catch(() => setProjects([])) : Promise.resolve(),
      crmService.getSubsidiaries().then(setSubsidiaries).catch(() => setSubsidiaries([])),
    ]).catch(console.error).finally(() => setLoading(false));
  }, [load, isHQ]);

  const isMe = useCallback((m) => (
    (m.user_id && m.user_id === me.id) ||
    (m.email && me.email && m.email.toLowerCase() === me.email.toLowerCase())
  ), [me]);

  const myRow = useMemo(() => team.find(isMe), [team, isMe]);
  const active = team.filter(m => m.status === 'active');
  const overallocated = active.filter(m => allocTotal(m) > m.capacity_pct);
  const staffedProjectIds = new Set(team.flatMap(m => (m.assignments || []).map(a => a.project_id)));
  const unstaffed = (future?.efforts || []).filter(p => !staffedProjectIds.has(p.id));

  // Tailor the default view to the viewer's own team; HQ oversees all by default
  useEffect(() => {
    if (groupFilter === null && !loading) {
      setGroupFilter(!isHQ && myRow?.team_group ? myRow.team_group : 'all');
    }
  }, [groupFilter, loading, isHQ, myRow]);

  const presentGroups = useMemo(() => {
    const keys = new Set(team.map(m => m.team_group).filter(Boolean));
    return [
      ...TEAM_GROUPS.filter(g => keys.has(g.key)),
      ...[...keys].filter(k => !TEAM_GROUPS.some(g => g.key === k)).map(k => ({ key: k, label: k })),
    ];
  }, [team]);

  const filteredTeam = useMemo(() => {
    if (!groupFilter || groupFilter === 'all') return team;
    if (groupFilter === 'unassigned') return team.filter(m => !m.team_group);
    return team.filter(m => m.team_group === groupFilter);
  }, [team, groupFilter]);

  const groupedSections = useMemo(() => {
    if (groupFilter && groupFilter !== 'all') return null;
    const order = [...presentGroups.map(g => g.key), null];
    return order
      .map(k => ({ key: k ?? 'unassigned', label: k ? groupLabel(k) : 'Unassigned', members: team.filter(m => (m.team_group || null) === k) }))
      .filter(s => s.members.length > 0);
  }, [team, groupFilter, presentGroups]);

  if (loading) {
    return <div style={{ color: '#94A3B8', textAlign: 'center', padding: 40 }}>Gathering the family…</div>;
  }

  return (
    <div style={{ padding: '16px 20px', maxWidth: 1180, margin: '0 auto' }}>
      {/* Summary tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div style={{ ...card, borderTop: '2px solid #22D3EE', padding: '12px 16px' }}>
          <div style={{ color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Family Members</div>
          <div style={{ color: '#F1F5F9', fontSize: 22, fontWeight: 700, marginTop: 4 }}>{active.length}</div>
        </div>
        <div style={{ ...card, borderTop: `2px solid ${overallocated.length ? '#EF4444' : '#10B981'}`, padding: '12px 16px' }}>
          <div style={{ color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Overallocated</div>
          <div style={{ color: overallocated.length ? '#EF4444' : '#F1F5F9', fontSize: 22, fontWeight: 700, marginTop: 4 }}>{overallocated.length}</div>
        </div>
        <div style={{ ...card, borderTop: `2px solid ${unstaffed.length ? '#c4a563' : '#10B981'}`, padding: '12px 16px' }}>
          <div style={{ color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Unstaffed Efforts</div>
          <div style={{ color: unstaffed.length ? '#c4a563' : '#F1F5F9', fontSize: 22, fontWeight: 700, marginTop: 4 }}>{unstaffed.length}</div>
        </div>
        <div style={{ ...card, borderTop: '2px solid #c4a563', padding: '12px 16px' }}>
          <div style={{ color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Open Positions</div>
          <div style={{ color: '#F1F5F9', fontSize: 22, fontWeight: 700, marginTop: 4 }}>
            {(future?.positions?.open_hire_needs || 0) + (future?.positions?.personnel_actions || 0)}
          </div>
          <div style={{ color: '#4B5563', fontSize: 9, marginTop: 2 }}>
            {future?.positions?.open_hire_needs || 0} flagged · {future?.positions?.personnel_actions || 0} {isHQ ? 'in Growth' : 'in hiring pipeline'}
          </div>
        </div>
      </div>

      {/* The future we're building */}
      {future && (
        <div style={{ ...card, borderLeft: '3px solid #c4a563', padding: '14px 18px', marginBottom: 16 }}>
          <div style={{ color: '#c4a563', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            The Future We're Building
          </div>
          <div style={{ color: '#94A3B8', fontSize: 12, lineHeight: 1.7, marginBottom: (future.efforts || []).length ? 10 : 0 }}>
            {future.coverage.white_space} of {future.coverage.total} customer technology gaps remain open today — {future.coverage.projected} already have moves in motion.
            {(future.efforts || []).length > 0 ? ' Every effort below closes part of that gap:' : ' Development efforts will appear here as they start.'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(future.efforts || []).map(p => {
              const ms = p.milestones || [];
              const done = ms.filter(m => m.done).length;
              return (
                <span key={p.id} title={(p.verticals || []).map(v => v.name).join(', ')} style={{
                  display: 'inline-flex', gap: 8, alignItems: 'baseline',
                  background: '#0A0F1C', border: `1px solid ${STAGE_COLORS[p.stage] || '#1E2D45'}55`,
                  borderRadius: 9, padding: '4px 12px', fontSize: 11,
                }}>
                  <span style={{ color: '#F1F5F9', fontWeight: 600 }}>{p.name}</span>
                  <span style={{ color: STAGE_COLORS[p.stage] || '#94A3B8', textTransform: 'capitalize', fontSize: 10 }}>{p.stage}</span>
                  {ms.length > 0 && <span style={{ color: '#6B7280', fontSize: 9 }}>{done}/{ms.length} ms</span>}
                  {p.target_date && <span style={{ color: '#6B7280', fontSize: 9 }}>→ {new Date(p.target_date).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}</span>}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Roster header + team pills */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ color: '#94A3B8', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 4 }}>
          Current State — Who We Are
        </div>
        {presentGroups.length > 0 && (
          <>
            <button onClick={() => setGroupFilter('all')} style={{
              padding: '3px 12px', borderRadius: 10, fontSize: 10, cursor: 'pointer',
              background: groupFilter === 'all' ? '#22D3EE18' : 'transparent',
              color: groupFilter === 'all' ? '#22D3EE' : '#6B7280',
              border: `1px solid ${groupFilter === 'all' ? '#22D3EE44' : '#1E2D45'}`,
              fontWeight: groupFilter === 'all' ? 600 : 400,
            }}>All Family</button>
            {presentGroups.map(g => (
              <button key={g.key} onClick={() => setGroupFilter(g.key)} style={{
                padding: '3px 12px', borderRadius: 10, fontSize: 10, cursor: 'pointer',
                background: groupFilter === g.key ? '#22D3EE18' : 'transparent',
                color: groupFilter === g.key ? '#22D3EE' : '#6B7280',
                border: `1px solid ${groupFilter === g.key ? '#22D3EE44' : '#1E2D45'}`,
                fontWeight: groupFilter === g.key ? 600 : 400,
              }}>{g.label}</button>
            ))}
            {team.some(m => !m.team_group) && (
              <button onClick={() => setGroupFilter('unassigned')} style={{
                padding: '3px 12px', borderRadius: 10, fontSize: 10, cursor: 'pointer',
                background: groupFilter === 'unassigned' ? '#22D3EE18' : 'transparent',
                color: groupFilter === 'unassigned' ? '#22D3EE' : '#6B7280',
                border: `1px solid ${groupFilter === 'unassigned' ? '#22D3EE44' : '#1E2D45'}`,
                fontWeight: groupFilter === 'unassigned' ? 600 : 400,
              }}>Unassigned</button>
            )}
          </>
        )}
        <span style={{ flex: 1 }} />
        {!myRow && !isHQ && (
          <button onClick={() => crmService.joinTeam().then(load).catch(e => alert(e.message))}
            style={{ padding: '6px 14px', background: '#0D1F3C', border: '1px solid #22D3EE44', color: '#22D3EE', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
            title="Links your login to the roster entry Headquarters created for your email">
            Claim My Profile
          </button>
        )}
        {!isHQ && (
          <button onClick={() => setRequestOpen(true)}
            style={{ padding: '6px 14px', background: 'none', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
            Request a Member
          </button>
        )}
        {isHQ && (
          <>
            <button onClick={() => setImportOpen(true)}
              title="Bulk import a company roster from CSV or Excel, with invitations"
              style={{ padding: '6px 14px', background: '#0D1F3C', border: '1px solid #22D3EE44', color: '#22D3EE', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
              ⬆ Import Team
            </button>
            <button onClick={() => setModal({})} style={{ padding: '6px 14px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer', fontWeight: 'bold' }}>
              + Add Member
            </button>
          </>
        )}
      </div>

      {team.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>
          <img src="/elephant-icon.svg" alt="" style={{ width: 56, height: 56, opacity: 0.2, marginBottom: 12 }} />
          <div style={{ fontSize: 14, color: '#94A3B8', marginBottom: 6 }}>The roster is empty</div>
          <div style={{ fontSize: 12 }}>
            {isHQ
              ? 'The herd is its people — add the family with their email and team, and each member fills in the rest.'
              : 'Headquarters builds the roster. Once you have been added, claim your profile to define your position.'}
          </div>
        </div>
      ) : groupedSections ? (
        groupedSections.map(s => (
          <div key={s.key} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 6px 2px' }}>
              {s.label} ({s.members.length})
            </div>
            {s.members.map(m => (
              <MemberRow key={m.id} member={m} isHQ={isHQ} isSelf={isMe(m)} projects={projects}
                onEdit={(mem) => setModal({ member: mem })} onChanged={load} />
            ))}
          </div>
        ))
      ) : (
        filteredTeam.map(m => (
          <MemberRow key={m.id} member={m} isHQ={isHQ} isSelf={isMe(m)} projects={projects}
            onEdit={(mem) => setModal({ member: mem })} onChanged={load} />
        ))
      )}

      {modal && (
        <MemberModal
          member={modal.member}
          subsidiaries={subsidiaries}
          isHQ={isHQ}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
      {requestOpen && <RequestMemberModal onClose={() => setRequestOpen(false)} />}
      {importOpen && <TeamImport onClose={() => setImportOpen(false)} onImported={load} />}
    </div>
  );
}
